import React, { useState } from 'react';
import { addLabel, updateLabel, removeLabel } from '../extensions/bundled/task-manager/boardLabels';
import { reorderLabels, nudgeLabel } from '../extensions/bundled/task-manager/boardEditStats';
import BoardLabelRow from './boards/BoardLabelRow';
import BoardLabelAddRow from './boards/BoardLabelAddRow';
import './boards/BoardLabelEditor.css';

/**
 * The board's label palette, edited in board settings (Boards Redesign cards
 * 5.2/5.3, 12.10.2026): one bordered list of rows (drag · colour · name ·
 * preview pill · usage count · remove) with the split new-label input as its
 * last row.
 *
 * Board-level rather than global: "blocked" means something different on an
 * API board and a design board, and one shared list across every board decays
 * into a hundred entries nobody prunes.
 *
 * Editing here changes every card carrying that label, because tasks store ids
 * (see boardLabels.js). That is the point of the indirection and the reason
 * rename is offered at all. The ORDER is an edit too: cards draw their chips
 * in palette order.
 *
 * `labels === null` means the palette is still being read (the modal was
 * opened from a grid tile, whose summary carries no palette) — the list says
 * so instead of showing an empty palette that a save would then write back.
 * `usage` is `{ [labelId]: taskCount }`, absent when the tasks are unknown.
 */
export default function BoardLabelEditor({ labels, usage, onChange, newId }) {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const loading = labels == null;
  const list = labels || [];
  const board = { labels: list };

  const drag = {
    dragId,
    overId,
    start: (id) => setDragId(id),
    over: (id) => setOverId(id),
    drop: (id) => {
      if (dragId && dragId !== id) onChange(reorderLabels(list, dragId, id));
      setDragId(null);
      setOverId(null);
    },
    end: () => {
      setDragId(null);
      setOverId(null);
    },
  };

  const add = ({ name, color }) => {
    const next = addLabel(board, { id: newId(), name, color });
    // addLabel returns the palette unchanged when the name already exists
    // (case-insensitively).
    if (next.length === list.length) return false;
    onChange(next);
    return true;
  };

  const count = list.length;
  return (
    <div className="board-label-editor">
      <div className="board-edit-section-head">
        <span className="board-edit-section-title">Labels</span>
        {count > 0 && (
          <span className="board-edit-section-hint">
            {count === 1 ? '1 label' : `${count} labels`}
            {count > 1 ? ' · drag to reorder' : ''}
          </span>
        )}
      </div>
      <div className="board-label-box">
        {loading && <div className="board-label-empty">Loading labels…</div>}
        {!loading && count === 0 && (
          <div className="board-label-empty">No labels yet — add one to categorise cards on this board.</div>
        )}
        {list.map((l) => (
          <BoardLabelRow
            key={l.id}
            label={l}
            count={usage ? usage[l.id] ?? 0 : null}
            drag={drag}
            onRename={(name) => onChange(updateLabel(board, l.id, { name }))}
            onColor={(color) => onChange(updateLabel(board, l.id, { color }))}
            onMove={(delta) => onChange(nudgeLabel(list, l.id, delta))}
            // Cards keep the id and simply stop showing the chip
            // (resolveLabels drops unknown ids) — deleting a label must not
            // rewrite every task that carried it.
            onRemove={() => onChange(removeLabel(board, l.id))}
          />
        ))}
        {!loading && <BoardLabelAddRow onAdd={add} />}
      </div>
    </div>
  );
}
